import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputSelect, SelectOptionType } from '../../../../../components';
import HeaderWithHelp from '../../../../../components/header-with-help';
import { TariffKindInternal } from '../../../../../data-access/gql-types/graphql';
import { leadingZero } from '../../../../../utils/helpers';
import { useGetTariffs } from '../../../hooks/tariffs/use-get-tariffs';
import { SetPricesData } from '../../channel-configuration/hooks/use-meter-tariff-prices-configuration';
import { getTariffDataByEnum } from '../../channel-configuration/utils';
import { CheckboxController } from '../../channel-details/optimizer/configuration/components/checkbox-controller';
import { TariffHours } from '../../channel-details/optimizer/configuration/components/tariff-hours';
import { HoursRangeFront, TariffBody, TariffFormDefault } from '../../channel-details/optimizer/configuration/types';
import { TariffName, TariffOperator } from '../../channel-details/optimizer/configuration/types/tariff-kind';
import { tariffsForRequest } from '../../channel-details/optimizer/configuration/utils/tariff-kind';
import { TariffHelp } from './tariff-help';
import { TariffPrefix } from './types';
import { tariffCheckboxesVisible } from './utils';

type TariffFormProps = {
  customTariff?: boolean;
  dynamicTariff?: boolean;
  availableTariffs?: TariffPrefix[];
  isDouble?: boolean;
  hidePricesInfo?: boolean;
  handleSetPrices?: (data: SetPricesData) => void;
};

export const TariffForm: React.FC<TariffFormProps> = ({
  customTariff = true,
  dynamicTariff = true,
  availableTariffs,
  isDouble,
  hidePricesInfo = false,
  handleSetPrices,
}) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const [defaultOperator, setDefaultOperator] = useState<string>('');
  const [defaultTariffName, setDefaultTariffName] = useState<string>('');
  const [defaultTariffParams, setDefaultTariffParams] = useState<TariffFormDefault>({
    hoursRanges: [],
    wholeWeekendCheaper: false,
    wholeHolidaysCheaper: false,
    ignoreSummerTimeChanges: false,
  });
  const { control, watch, setValue } = useFormContext<TariffBody>();
  const { allTariffs } = useGetTariffs();
  const { operator, tariffName, hoursRanges } = watch();

  useEffect(() => {
    const kind = tariffsForRequest.find(
      (x) => x.tariff.toLowerCase() === tariffName && x.operator.toLowerCase() === operator,
    )?.enum;

    if (kind) {
      const tariffData = getTariffDataByEnum(allTariffs, kind as TariffKindInternal);

      if (tariffData) {
        const hours = tariffData.hoursRanges.map((x) => ({
          ...x,
          from: `${leadingZero(x.from)}:00`,
          to: `${leadingZero(x.to)}:00`,
        }));
        const wholeWeekendCheaper = !!tariffData.wholeWeekendCheaper;
        const wholeHolidaysCheaper = !!tariffData.wholeHolidaysCheaper;
        const ignoreSummerTimeChanges = !!tariffData.ignoreSummerTimeChanges;

        setValue('hoursRanges', hours);
        setValue('wholeWeekendCheaper', wholeWeekendCheaper);
        setValue('wholeHolidaysCheaper', wholeHolidaysCheaper);
        setValue('ignoreSummerTimeChanges', ignoreSummerTimeChanges);
        setDefaultTariffName(tariffName);
        setDefaultOperator(operator);
        setDefaultTariffParams({
          hoursRanges: hours,
          wholeWeekendCheaper,
          wholeHolidaysCheaper,
          ignoreSummerTimeChanges,
        });

        handleSetPrices?.({
          selectedTariff: kind as TariffKindInternal,
          hoursRanges: tariffData.hoursRanges,
        });

        return;
      }

      if (!customTariff) {
        setValue('hoursRanges', []);
        setValue('wholeWeekendCheaper', false);
        setValue('wholeHolidaysCheaper', false);
        setValue('ignoreSummerTimeChanges', false);
        handleSetPrices?.({ selectedTariff: null, hoursRanges: null });
      }
    }
  }, [operator, tariffName, allTariffs, customTariff]);

  const handleWatchForCustom = (values: TariffBody) => {
    if (defaultOperator && defaultTariffName) {
      const toCompare = {
        hoursRanges: values.hoursRanges,
        wholeWeekendCheaper: values.wholeWeekendCheaper,
        wholeHolidaysCheaper: values.wholeHolidaysCheaper,
        ignoreSummerTimeChanges: values.ignoreSummerTimeChanges,
      };

      if (JSON.stringify(toCompare) !== JSON.stringify(defaultTariffParams)) {
        setValue('operator', TariffOperator.Custom);
        setValue('tariffName', TariffName.Custom);
      } else {
        setValue('operator', defaultOperator);
        setValue('tariffName', defaultTariffName);
      }
    }
  };

  const operators: SelectOptionType<string>[] = useMemo(() => {
    const operators = allTariffs.map((x) => ({ label: x.operator, value: x.operator.toLowerCase() }));

    return [
      ...operators,
      { label: t('optimizer.configuration.tariff.custom'), value: TariffOperator.Custom, disabled: true },
    ];
  }, [allTariffs]);

  const getTariffLabel = (tariffPrefix: TariffPrefix) => {
    switch (tariffPrefix) {
      case TariffPrefix.G13S:
        return t('optimizer.configuration.tariff.g13s');
      default:
        return tariffPrefix;
    }
  };

  const operatorTariffs: SelectOptionType<string>[] = useMemo(() => {
    const tariffs =
      allTariffs
        .find((x) => x.operator.toLowerCase() === operator)
        ?.tariffs.filter((z) => {
          if (availableTariffs) {
            return availableTariffs.includes(z.name as TariffPrefix);
          }

          return true;
        })
        .map((y) => ({
          label: getTariffLabel(y.name as TariffPrefix),
          value: y.name.toLowerCase(),
        })) || [];

    if (dynamicTariff) {
      tariffs.push({ label: t('optimizer.configuration.tariff.dynamic'), value: TariffName.Dynamic });
    }

    return customTariff
      ? [...tariffs, { label: t('optimizer.configuration.tariff.custom'), value: TariffName.Custom, disabled: true }]
      : [...tariffs];
  }, [allTariffs, operator, customTariff, dynamicTariff, availableTariffs]);

  const checkboxesVisible = tariffCheckboxesVisible({ tariffName, isDouble });

  return (
    <HeaderWithHelp
      kind="sub-header"
      name="tariff"
      headerTitle={t('optimizer.configuration.tariff.label')}
      helpTitle={t('optimizer.configuration.tariff.label')}
      helpContent={<TariffHelp />}
      hidden={isDouble}
    >
      <div className="grid-list-24">
        <Controller
          name="operator"
          control={control}
          rules={{
            required: tc('isRequired') as string,
          }}
          render={({ field, fieldState: { error } }) => (
            <InputSelect
              onChange={field.onChange}
              value={field.value}
              options={operators}
              label={t('optimizer.configuration.tariff.operator')}
              name={field.name}
              {...(error ? { errorMessage: error.message } : {})}
            />
          )}
        />

        {operator && (
          <Controller
            name="tariffName"
            control={control}
            rules={{
              required: tc('isRequired') as string,
            }}
            render={({ field, fieldState: { error } }) => (
              <InputSelect
                onChange={field.onChange}
                value={field.value}
                disabled={operator === TariffOperator.Custom}
                options={operatorTariffs}
                label={t('optimizer.configuration.tariff.label')}
                name={field.name}
                {...(error ? { errorMessage: error.message } : {})}
              />
            )}
          />
        )}
      </div>

      {hoursRanges.length || checkboxesVisible ? <hr className="m-b-24" /> : null}

      {tariffName && !hidePricesInfo && (
        <>
          {hoursRanges.map((range: HoursRangeFront, index: number) => (
            <TariffHours
              key={index}
              index={index}
              range={range}
              timeZoneKind={range.timeZoneKind}
              handleWatchForCustom={handleWatchForCustom}
              editEnabled={customTariff}
            />
          ))}
          {checkboxesVisible && (
            <>
              <CheckboxController
                name="wholeWeekendCheaper"
                label={t('optimizer.configuration.tariff.wholeWeekendCheaper')}
                handleWatchForCustom={handleWatchForCustom}
                disabled={!customTariff}
              />
              <CheckboxController
                name="wholeHolidaysCheaper"
                label={t('optimizer.configuration.tariff.wholeHolidaysCheaper')}
                handleWatchForCustom={handleWatchForCustom}
                disabled={!customTariff}
              />
              <CheckboxController
                name="ignoreSummerTimeChanges"
                label={t('optimizer.configuration.tariff.ignoreSummerTimeChanges')}
                handleWatchForCustom={handleWatchForCustom}
                disabled={!customTariff}
              />
            </>
          )}
        </>
      )}
    </HeaderWithHelp>
  );
};
